// AudioWaveform.tsx - リアルタイムのオーディオ波形可視化コンポーネント
import { useEffect, useRef, useState } from "react";
import { recordingSession } from "../services/recordingSession";
import { useAppStore } from "../store/appStore";

interface AudioWaveformProps {
	height?: number;
	barWidth?: number;
	barGap?: number;
	barColor?: string;
	backgroundColor?: string;
	gradientColors?: string[]; // グラデーション色の配列
	animated?: boolean; // アイドル状態でもアニメーションさせる
}

export const AudioWaveform = ({
	height = 100, // Increased height
	barWidth = 3,
	barGap = 1,
	barColor = "#3b82f6",
	backgroundColor = "transparent",
	gradientColors,
	animated = true,
}: AudioWaveformProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const animationRef = useRef<number | undefined>(undefined);
	const analyzerRef = useRef<AnalyserNode | null>(null);
	const dataArrayRef = useRef<Uint8Array | null>(null);
	const interimText = useAppStore((state) => state.interimText); // Get interim text for display
	const isRecording = useAppStore((state) => state.isRecording); // Get recording state
	const [idleAnimationPhase, setIdleAnimationPhase] = useState(0);

	// アイドルアニメーションのフェーズを更新
	useEffect(() => {
		if (animated && !isRecording) {
			const interval = setInterval(() => {
				setIdleAnimationPhase((prev) => (prev + 0.05) % (Math.PI * 2));
			}, 50);
			return () => clearInterval(interval);
		}
	}, [animated, isRecording]);

	// 録音セッションのアナライザを参照する
	// （AudioContext とストリームは recordingSession が所有・解放する。
	//   isRecording はセッション準備完了後に true になるため、ここでは同期的に取得できる）
	useEffect(() => {
		if (isRecording) {
			const analyser = recordingSession.getAnalyser();
			analyzerRef.current = analyser;
			dataArrayRef.current = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
		} else {
			analyzerRef.current = null;
			dataArrayRef.current = null;
		}
	}, [isRecording]);

	// 波形の描画
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		// キャンバスサイズの設定
		const dpr = window.devicePixelRatio || 1;
		canvas.width = canvas.clientWidth * dpr;
		canvas.height = canvas.clientHeight * dpr;
		ctx.scale(dpr, dpr);

		// グラデーションの設定
		let gradient: CanvasGradient | null = null;
		if (gradientColors && gradientColors.length > 1) {
			gradient = ctx.createLinearGradient(0, 0, 0, height);
			const step = 1 / (gradientColors.length - 1);
			gradientColors.forEach((color, i) => {
				if (gradient) {
					gradient.addColorStop(i * step, color);
				}
			});
		}

		const draw = () => {
			// キャンバスを完全にクリア
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			// 背景色を設定
			ctx.fillStyle = backgroundColor;
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			const canvasWidth = canvas.clientWidth;
			const barCount = Math.floor(canvasWidth / (barWidth + barGap));

			// 録音中はオーディオデータを使用した波形を表示
			if (isRecording && analyzerRef.current && dataArrayRef.current) {
				// オーディオデータを取得
				analyzerRef.current.getByteFrequencyData(dataArrayRef.current);
				for (let i = 0; i < barCount; i++) {
					const dataIndex = Math.floor((i * dataArrayRef.current.length) / barCount);
					const normalizedValue = dataArrayRef.current[dataIndex] / 255;
					// 最低高さを2pxに設定
					const minBarHeight = 2;
					const barHeight = Math.max(normalizedValue * height * 0.8, minBarHeight);
					const x = i * (barWidth + barGap);
					const centerY = height / 2;
					const halfBarHeight = barHeight / 2;
					ctx.fillStyle = gradient || barColor;
					ctx.fillRect(x, centerY - halfBarHeight, barWidth, barHeight);
				}
			}
			// アイドル状態ではアニメーション波形を表示
			else {
				for (let i = 0; i < barCount; i++) {
					// サイン波でアニメーション
					const phase = idleAnimationPhase + (i * Math.PI) / 16;
					const sinValue = Math.sin(phase) * 0.3 + 0.5; // 0.2-0.8の範囲に変換
					const barHeight = sinValue * height * 0.5;

					const x = i * (barWidth + barGap);
					const y = (height - barHeight) / 2;

					// バーの描画
					ctx.fillStyle = gradient || barColor;
					ctx.fillRect(x, y, barWidth, barHeight);
				}
			}

			animationRef.current = requestAnimationFrame(draw);
		};

		draw();

		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current);
				animationRef.current = undefined;
			}
		};
	}, [height, barWidth, barGap, barColor, backgroundColor, idleAnimationPhase, gradientColors, isRecording]);

	return (
		<div
			style={{
				width: "100%",
				height: `${height}px`,
				position: "relative",
				overflow: "hidden",
			}}
		>
			<canvas
				ref={canvasRef}
				className="w-full h-full rounded-lg"
				style={{
					display: "block",
					width: "100%",
					height: "100%",
				}}
			/>

			{interimText && (
				<div
					className="absolute inset-0 flex items-center justify-center pointer-events-none"
					style={{
						zIndex: 10,
						background: "rgba(0, 0, 0, 0.3)",
						backdropFilter: "blur(2px)",
						borderRadius: "0.5rem",
					}}
				>
					<div
						className="text-white text-xl md:text-2xl font-medium px-4 py-2 text-center"
						style={{
							textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
							maxWidth: "100%",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{interimText}
					</div>
				</div>
			)}
		</div>
	);
};
